import { scaleLinear } from 'd3-scale';

const GRAMMATICAL_STOP_WORDS = new Set([
  'a', 'an', 'the', 'in', 'on', 'at', 'for', 'to', 'of', 'with', 'by', 'and', 'or', 'is', 'are', 
  'it', 'this', 'that', 'from', 'as'
]);

const INTENT_KEYWORDS = {
  transactional: ['buy', 'price', 'cost', 'cheap', 'discount', 'sale', 'deal', 'order', 'purchase', 'pricing'],
  commercial: ['best', 'top', 'vs', 'review', 'comparison', 'rated', 'guide', 'alternative'],
  informational: ['how', 'what', 'why', 'when', 'tutorial', 'tips', 'examples', 'definition', 'learn'],
  navigational: ['login', 'signin', 'support', 'contact', 'location', 'near me', 'official']
};

export const detectColumns = (headers) => {
  const map = {
    keyword: null,
    volume: null,
    position: null,
    cpc: null,
    competition: null,
    difficulty: null,
    url: null,
    clicks: null,
    impressions: null
  };

  headers.forEach(h => {
    const lower = h.toLowerCase().trim();
    if (['keyword', 'query', 'term', 'phrase'].some(k => lower.includes(k))) map.keyword = h;
    if (['volume', 'search volume', 'vol', 'avg. monthly searches'].some(k => lower.includes(k))) map.volume = h;
    if (['position', 'rank', 'ranking', 'current rank'].some(k => lower === k || lower.includes('position'))) map.position = h;
    if (['cpc', 'cost per click', 'cost-per-click'].some(k => lower.includes(k))) map.cpc = h;
    if (['competition', 'comp.', 'competitive density'].some(k => lower.includes(k))) map.competition = h;
    if (['difficulty', 'kd', 'keyword difficulty'].some(k => lower.includes(k))) map.difficulty = h;
    if (['url', 'landing page', 'page', 'target url'].some(k => lower.includes(k))) map.url = h;
    if (['clicks', 'traffic'].some(k => lower.includes(k))) map.clicks = h;
    if (['impressions', 'views'].some(k => lower.includes(k))) map.impressions = h;
  });

  return map;
};

// Logic to generate a topic suggestion based on keyword patterns
const getTopicSuggestion = (keyword, intent) => {
  const kw = keyword.toLowerCase();
  
  if (intent === 'Commercial') {
    if (kw.includes('vs')) return `The Ultimate Showdown: ${keyword.split('vs').map(s => s.trim()).join(' vs ')} - Which Is Better?`;
    if (kw.includes('best') || kw.includes('top')) return `Expert Roundup: Why ${keyword} Stands Out in 2024 (Ranked & Reviewed)`;
    return `The Definitive Buyer's Guide: Everything You Need to Know Before Choosing ${keyword}`;
  }
  
  if (intent === 'Transactional') {
    if (kw.includes('price') || kw.includes('cost')) return `Is it Worth It? A Deep Dive into ${keyword} ROI and Market Pricing`;
    return `7 Proven Ways to Get the Best Deal on ${keyword} (With Real-World Savings)`;
  }

  return `Maximize Your Results: The Complete Strategy for Mastering ${keyword}`;
};

export const analyzeData = (data, columnMap) => {
  const tokenStats = {};
  const urlStats = {};
  const processedKeywords = [];
  const intentGroups = { Transactional: [], Commercial: [], Informational: [], Navigational: [], General: [] };
  
  data.forEach(row => {
    const keyword = row[columnMap.keyword]?.toString().toLowerCase().trim();
    if (!keyword) return;

    const volume = parseInt(row[columnMap.volume] || 0, 10);
    const position = parseInt(row[columnMap.position] || 100, 10);
    const cpc = parseFloat(row[columnMap.cpc] || 0);
    const competition = parseFloat(row[columnMap.competition] || 0);
    const clicks = parseInt(row[columnMap.clicks] || 0, 10);
    const url = row[columnMap.url] || '';

    // Intent Logic
    let intent = 'General';
    for (const [type, words] of Object.entries(INTENT_KEYWORDS)) {
      if (words.some(w => keyword.includes(w))) {
        intent = type.charAt(0).toUpperCase() + type.slice(1);
        break;
      }
    }

    const kwObj = {
      keyword,
      volume,
      position,
      cpc,
      competition,
      clicks,
      url,
      intent,
      trafficValue: volume * cpc,
      topicSuggestion: getTopicSuggestion(keyword, intent)
    };

    processedKeywords.push(kwObj);
    intentGroups[intent].push(kwObj);

    // URL Aggregation
    if (url) {
      if (!urlStats[url]) {
        urlStats[url] = { url, totalVolume: 0, totalClicks: 0, keywords: [], avgPos: 0 };
      }
      urlStats[url].totalVolume += volume;
      urlStats[url].totalClicks += clicks;
      urlStats[url].keywords.push(kwObj);
    }

    // Tokenize
    const tokens = keyword.split(/\s+/).filter(t => !GRAMMATICAL_STOP_WORDS.has(t) && t.length > 2);
    tokens.forEach(token => {
      if (!tokenStats[token]) {
        tokenStats[token] = { token, frequency: 0, totalVolume: 0, totalClicks: 0, positions: [] };
      }
      const stat = tokenStats[token];
      stat.frequency += 1;
      stat.totalVolume += volume;
      stat.totalClicks += clicks;
      stat.positions.push(position);
    });
  });

  const tokensArray = Object.values(tokenStats).map(t => ({
    ...t,
    avgPosition: t.positions.reduce((a, b) => a + b, 0) / t.positions.length,
    opportunity: (t.totalVolume * Math.log10(t.frequency + 1))
  }));

  const urlsArray = Object.values(urlStats).map(u => ({
    ...u,
    avgPos: u.keywords.length > 0 ? (u.keywords.reduce((a, b) => a + b.position, 0) / u.keywords.length) : 0
  }));

  return {
    keywords: processedKeywords,
    tokens: tokensArray,
    urls: urlsArray,
    intentGroups,
    stats: {
      totalKeywords: processedKeywords.length,
      totalVolume: processedKeywords.reduce((acc, k) => acc + k.volume, 0),
      avgPosition: processedKeywords.length > 0 ? (processedKeywords.reduce((acc, k) => acc + k.position, 0) / processedKeywords.length) : 0,
      totalTrafficValue: processedKeywords.reduce((acc, k) => acc + k.trafficValue, 0)
    }
  };
};

export const generateInsights = (analysis) => {
  const { tokens, keywords, intentGroups, urls } = analysis;

  const domainMap = {};
  urls.forEach(u => {
    try {
      const domain = new URL(u.url.startsWith('http') ? u.url : `https://${u.url}`).hostname;
      if (!domainMap[domain]) domainMap[domain] = { domain, clicks: 0, volume: 0, urlCount: 0 };
      domainMap[domain].clicks += u.totalClicks;
      domainMap[domain].volume += u.totalVolume;
      domainMap[domain].urlCount += 1;
    } catch (e) { /* invalid url */ }
  });

  const topDomains = Object.values(domainMap).sort((a, b) => b.clicks - a.clicks);

  return {
    topUrls: urls
      .sort((a, b) => b.totalClicks - a.totalClicks || b.totalVolume - a.totalVolume)
      .slice(0, 10),

    trafficGaps: keywords
      .filter(k => k.position > 3 && k.position <= 20)
      .map(k => ({
        ...k,
        lostTraffic: k.volume * 0.25,
        strategy: k.position > 10 ? 'Content Expansion' : 'Backlink/UX Push'
      }))
      .sort((a, b) => b.lostTraffic - a.lostTraffic)
      .slice(0, 8),

    highValueIntent: [...intentGroups.Transactional, ...intentGroups.Commercial]
      .sort((a, b) => b.volume - a.volume)
      .slice(0, 12),

    intentInsights: Object.entries(intentGroups)
      .map(([intent, kws]) => ({
        intent,
        count: kws.length,
        volume: kws.reduce((a, b) => a + b.volume, 0),
        topUrl: kws.sort((a, b) => b.volume - a.volume)[0]?.url || 'N/A'
      }))
      .filter(i => i.count > 0),

    cpcLeaders: keywords
      .filter(k => k.cpc > 0)
      .sort((a, b) => b.cpc - a.cpc)
      .slice(0, 6),

    marketShare: topDomains.slice(0, 5)
  };
};